import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../../design/Icon';
import { fmt } from '../../design/format';
import { Page, useOrgName } from '../../layout';
import { scopeLabel as filterScopeLabel, useShellStatus, useTerms } from '../../layout/ShellStatus';
import { useHierarchyFilter } from '../../hooks/useHierarchyFilter';
import { LanguagePill, SentimentBadge } from '../../components/shared/Badges';
import { RouteStrip } from '../../components/shared/People';
import { MenuSeparator, MenuTitle, Popover, usePopover, useToast } from '../../components/shared/Overlay';
import { EmptyState, ErrorState, Skeleton } from '../../components/shared/States';
import { callsApi } from '../../services/callsApi';
import { useAnalytics } from '../../services/analyticsApi';
import { CallFilterChips, ClassificationCell } from './CallFilterChips';
import { callerDirectoryLine, entryTypeLabel } from '../admin/directoryModel';
import {
  BUILT_IN_VIEWS,
  CALLS_STORAGE_KEY,
  COLUMNS_STORAGE_KEY,
  EMPTY_CALL_FILTERS,
  OPTIONAL_COLUMNS,
  optionalColumnLabel,
  PAGE_SIZE,
  VIEWS_STORAGE_KEY,
  callExportParams,
  callListParams,
  displaySentiment,
  filtersFromQuery,
  languageCode,
  languageName,
  linkedLabel,
  normalizeFilters,
  readStored,
  resolveView,
  viewName,
  writeStored,
} from './callFilters';
import { fmtDay, fmtTime, routeKind } from './callFormat';

const ATTRIBUTION_FROM_CALLER = 'AnalysisExtraction';

function SavedViewRow({ view, onPick, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(view.name);
  const save = () => {
    const next = name.trim();
    if (next && next !== view.name) onRename(view.name, next);
    setEditing(false);
  };
  if (editing)
    return (
      <div className="row" style={{ gap: 4, padding: '2px 4px' }}>
        <input
          className="input grow"
          style={{ height: 30 }}
          aria-label={`New name for ${view.name}`}
          value={name}
          maxLength={120}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') {
              e.stopPropagation();
              setEditing(false);
            }
          }}
        />
        <button type="button" className="btn btn-primary btn-sm" onClick={save}>
          Save
        </button>
      </div>
    );
  return (
    <div className="row" style={{ gap: 2 }}>
      <button type="button" className="menu-item grow" onClick={() => onPick(view.filters)}>
        <Icon name="list" />
        {view.name}
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-sm icon-btn"
        aria-label={`Rename ${view.name}`}
        title="Rename"
        onClick={() => {
          setName(view.name);
          setEditing(true);
        }}
      >
        <Icon name="edit" className="ico-sm" />
      </button>
      <button type="button" className="btn btn-ghost btn-sm icon-btn" aria-label={`Delete ${view.name}`} title="Delete" onClick={() => onDelete(view.name)}>
        <Icon name="trash" className="ico-sm" />
      </button>
    </div>
  );
}

function SavedViews({ categories, views, onPick, onRename, onDelete }) {
  const pop = usePopover();
  const builtIn = BUILT_IN_VIEWS.map((v) => ({ name: v.name, filters: resolveView(v, categories) })).filter((v) => v.filters);
  const pick = (filters) => {
    pop.close();
    onPick(filters);
  };
  return (
    <Popover
      open={pop.open}
      onClose={pop.close}
      align="right"
      width={300}
      label="Saved views"
      anchor={
        <button className="btn" type="button" id="views-btn" aria-haspopup="true" aria-expanded={pop.open} onClick={pop.toggle}>
          <Icon name="list" />
          Saved views
          <Icon name="chevD" className="ico-sm" />
        </button>
      }
    >
      <MenuTitle>Saved views</MenuTitle>
      {views.length + builtIn.length === 0 && <div className="caption" style={{ padding: '4px 9px' }}>No saved views yet</div>}
      {views.map((v) => (
        <SavedViewRow key={v.name} view={v} onPick={pick} onRename={onRename} onDelete={onDelete} />
      ))}
      {views.length > 0 && builtIn.length > 0 && <MenuSeparator />}
      {builtIn.map((v) => (
        <button key={v.name} type="button" className="menu-item" onClick={() => pick(v.filters)}>
          <Icon name="list" />
          {v.name}
        </button>
      ))}
    </Popover>
  );
}

function ColumnsMenu({ hidden, onChange }) {
  const pop = usePopover();
  const terms = useTerms();
  return (
    <Popover
      open={pop.open}
      onClose={pop.close}
      align="right"
      width={220}
      label="Columns"
      anchor={
        <button className="btn btn-ghost btn-sm" type="button" id="cols" aria-haspopup="true" aria-expanded={pop.open} onClick={pop.toggle}>
          <Icon name="columns" className="ico-sm" />
          Columns
        </button>
      }
    >
      <MenuTitle>Columns</MenuTitle>
      <div className="tree">
        {OPTIONAL_COLUMNS.map(([key, label]) => (
          <label key={key} className="tree-row">
            <input type="checkbox" checked={!hidden.includes(key)} onChange={(e) => onChange(e.target.checked ? hidden.filter((k) => k !== key) : [...hidden, key])} />
            {optionalColumnLabel(key, label, terms)}
          </label>
        ))}
      </div>
    </Popover>
  );
}

function CallRow({ call, show, onOpen }) {
  const terms = useTerms();
  const sentiment = displaySentiment(call.callerSentimentEnd);
  const language = languageName(call.language);
  const unanswered = call.outcome === 'Voicemail' ? 'Voicemail' : 'Not answered';
  const listed = call.callerDirectory;
  return (
    <tr className="clickable" data-testid="call-row" tabIndex={0} onClick={() => onOpen(call)} onKeyDown={(e) => e.key === 'Enter' && onOpen(call)}>
      <td className="nowrap">
        <div className="cell-title row" style={{ gap: 6 }}>
          <Icon name={{ Outbound: 'outbound', Internal: 'users' }[call.direction] || 'inbound'} className="ico-sm muted" />
          {fmtTime(call.startTime)}
        </div>
        <div className="cell-sub">{fmtDay(call.startTime)}</div>
      </td>
      <td>
        <div className="cell-title nowrap">
          {listed?.fullName || call.callerName || call.callerNumber || 'Unknown caller'}
          {listed?.isInternal && (
            <>
              {' '}
              <span className="badge b-info">{entryTypeLabel(listed.entryType)}</span>
            </>
          )}
        </div>
        <div className="cell-sub num nowrap">{listed?.fullName || call.callerName ? call.callerNumber : ''}</div>
        {listed && !listed.isInternal && <div className="cell-sub">{callerDirectoryLine(listed)}</div>}
      </td>
      {show.community && (
        <td>
          <div className="nowrap">{call.communityName || <span className="muted">No {terms.communityLower}</span>}</div>
          <div className="cell-sub">
            {call.hierarchyNodeName}
            {call.communityAttributionSource === ATTRIBUTION_FROM_CALLER && (
              <>
                {' · '}
                <span title="Identified from what the caller said">from caller</span>
              </>
            )}
          </div>
        </td>
      )}
      {show.route && (
        <td>
          <RouteStrip steps={(call.routeSteps || []).map((s) => ({ type: routeKind(s.type), name: s.entityName }))} />
        </td>
      )}
      {show.handled && (
        <td>
          <div className="nowrap">{call.agentName || <span className="muted">{unanswered}</span>}</div>
          <div className="cell-sub nowrap">{call.queueName}</div>
        </td>
      )}
      {show.duration && (
        <td className="n nowrap">
          {fmt.mmss(call.duration)}
          {call.outcome === 'Abandoned' && (
            <div className="cell-sub" style={{ color: 'var(--critical)' }}>
              Abandoned
            </div>
          )}
          {call.outcome === 'Transferred' && <div className="cell-sub">Transferred</div>}
        </td>
      )}
      {show.classifications && (
        <td>
          <div className="row-wrap" style={{ gap: 4, maxWidth: 300 }}>
            <ClassificationCell call={call} />
          </div>
        </td>
      )}
      {show.sentiment && (
        <td>
          <SentimentBadge sentiment={sentiment} />
        </td>
      )}
      {show.language && <td>{language && <LanguagePill code={languageCode(call.language)} title={language} />}</td>}
      {show.transcript && (
        <td>
          {call.hasTranscript && (
            <span title="Transcript available" style={{ color: 'var(--accent-ink)' }}>
              <Icon name="file" className="ico-sm" />
            </span>
          )}
        </td>
      )}
    </tr>
  );
}

/** Call log (mockup 12-calls.html): AI filters, route strip per call, saved views, CSV/.xlsx export. */
export function CallsPage() {
  const org = useOrgName();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const h = useHierarchyFilter();
  const { status: shellStatus, terms } = useShellStatus();
  const [filters, setFilters] = useState(() => filtersFromQuery(location.search) || normalizeFilters({ ...readStored(CALLS_STORAGE_KEY, EMPTY_CALL_FILTERS), page: 0 }));
  const [categories, setCategories] = useState([]);
  const [views, setViews] = useState(() => readStored(VIEWS_STORAGE_KEY, [], 'localStorage'));
  const [hidden, setHidden] = useState(() => readStored(COLUMNS_STORAGE_KEY, [], 'localStorage'));

  const update = useCallback((patch) => {
    setFilters((current) => {
      const next = normalizeFilters({ ...current, page: 0, ...patch });
      writeStored(CALLS_STORAGE_KEY, next);
      return next;
    });
  }, []);

  const linked = useMemo(() => filtersFromQuery(location.search), [location.search]);
  useEffect(() => {
    if (!linked) return;
    setFilters(linked);
    writeStored(CALLS_STORAGE_KEY, linked);
    navigate(location.pathname, { replace: true, state: location.state });
  }, [linked, location.pathname, location.state, navigate]);

  const searchFromTopbar = location.state?.search;
  useEffect(() => {
    if (typeof searchFromTopbar !== 'string') return;
    update({ q: searchFromTopbar });
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [searchFromTopbar, location.key, location.pathname, location.search, navigate, update]);

  useEffect(() => {
    let cancelled = false;
    callsApi
      .classifications()
      .then((taxonomy) => !cancelled && setCategories(taxonomy?.categories || []))
      .catch(() => !cancelled && setCategories([]));
    return () => {
      cancelled = true;
    };
  }, []);

  const params = useMemo(() => callListParams(filters, h.params), [filters, h.params]);
  const paramsKey = JSON.stringify(params);

  const list = useAnalytics(() => callsApi.list(JSON.parse(paramsKey)), paramsKey);
  const state = { status: list.error ? 'error' : list.loading ? 'loading' : 'ready', data: list.data, error: list.error };

  const show = Object.fromEntries(OPTIONAL_COLUMNS.map(([key]) => [key, !hidden.includes(key)]));
  const calls = state.data?.calls || [];
  const total = state.data?.pagination?.totalRecords || 0;
  const pageCount = Math.max(1, state.data?.pagination?.totalPages || 1);
  const first = filters.page * PAGE_SIZE + 1;
  const clsNames = filters.cls.map((id) => categories.find((c) => c.id === id)?.name).filter(Boolean);
  const scopeLabel = filterScopeLabel(h, shellStatus);
  const queueLabel = linkedLabel(filters.queueIds, filters.queueName, calls, 'queue');
  const agentLabel = linkedLabel(filters.agentIds, filters.agentName, calls, 'agent');
  const countSub = `${scopeLabel}, ${h.range.rangeLabel}${clsNames.length ? `, classification ${filters.match === 'any' ? 'matches any of ' : 'matches all of '}${clsNames.join(', ')}` : ''}${queueLabel ? `, queue ${queueLabel}` : ''}${agentLabel ? `, handled by ${agentLabel}` : ''}`;

  const saveView = () => {
    const name = viewName(filters, categories, h.filter.nodeId || h.filter.communityIds.length ? h.label : '');
    storeViews([{ name, filters: { ...filters, page: 0 } }, ...views.filter((v) => v.name !== name)].slice(0, 20));
    toast(`Saved view “${name}” for you`);
  };

  const storeViews = (next) => {
    setViews(next);
    writeStored(VIEWS_STORAGE_KEY, next, 'localStorage');
  };

  const renameView = (from, to) => {
    if (views.some((v) => v.name === to)) {
      toast(`A view named “${to}” already exists`, 'alert');
      return;
    }
    storeViews(views.map((v) => (v.name === from ? { ...v, name: to } : v)));
    toast(`Renamed view to “${to}”`);
  };

  const deleteView = (name) => {
    storeViews(views.filter((v) => v.name !== name));
    toast(`Deleted view “${name}”`);
  };

  const setHiddenColumns = (next) => {
    setHidden(next);
    writeStored(COLUMNS_STORAGE_KEY, next, 'localStorage');
  };

  const actions = (
    <>
      <SavedViews categories={categories} views={views} onPick={(f) => update({ ...EMPTY_CALL_FILTERS, ...f })} onRename={renameView} onDelete={deleteView} />
      <button className="btn" type="button" id="save-view" onClick={saveView}>
        Save view
      </button>
    </>
  );

  let body;
  if (state.status === 'error') {
    body = (
      <ErrorState
        title="Calls couldn't load"
        body="The call search service returned an error. Your filters are kept; retry in a moment."
        error={state.error}
        onRetry={list.retry}
      />
    );
  } else if (state.status === 'ready' && total === 0) {
    body = (
      <EmptyState
        icon="search"
        title="No calls match these filters"
        body={`Nothing in ${h.range.rangeLabel} matches every filter you set. Remove a classification or switch Match all to Match any.`}
        actions={
          <>
            <button className="btn" type="button" onClick={() => update(EMPTY_CALL_FILTERS)}>
              Clear call filters
            </button>
            <button className="btn btn-primary" type="button" onClick={() => h.setPreset('last30')}>
              Last 30 days
            </button>
          </>
        }
      />
    );
  } else {
    body = (
      <section className="card">
        <div className="card-head" style={{ alignItems: 'center' }}>
          <div>
            <div className="t" id="count">
              {state.data ? `${fmt.int(total)} ${total === 1 ? 'call' : 'calls'}` : 'Calls'}
            </div>
            <div className="d">{countSub}</div>
          </div>
          <div className="acts">
            <ColumnsMenu hidden={hidden} onChange={setHiddenColumns} />
          </div>
        </div>
        <div className="card-body flush">
          {!state.data ? (
            <Skeleton height={420} />
          ) : (
            <div className="table-wrap">
              <table className="table" id="tbl" aria-busy={state.status === 'loading' || undefined}>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Caller</th>
                    {show.community && <th>{terms.community}</th>}
                    {show.route && <th>Route</th>}
                    {show.handled && <th>Handled by</th>}
                    {show.duration && <th className="n">Duration</th>}
                    {show.classifications && <th>Classifications</th>}
                    {show.sentiment && <th>Sentiment</th>}
                    {show.language && <th title="Language detected from the recording">Lang.</th>}
                    {show.transcript && (
                      <th title="Transcript available">
                        <span className="sr-only">Transcript</span>
                        <Icon name="file" className="ico-sm" />
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call) => (
                    <CallRow key={call.id} call={call} show={show} onOpen={(c) => navigate(`/calls/${encodeURIComponent(c.id)}`)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="table-foot">
          <span id="range">{calls.length ? `Showing ${fmt.int(first)}–${fmt.int(first + calls.length - 1)} of ${fmt.int(total)}` : ''}</span>
          <div className="pager">
            <button className="btn btn-sm" type="button" id="prev" disabled={filters.page === 0} onClick={() => update({ page: filters.page - 1 })}>
              Previous
            </button>
            <button className="btn btn-sm" type="button" id="next" disabled={filters.page >= pageCount - 1} onClick={() => update({ page: filters.page + 1 })}>
              Next
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <Page
      title="Calls"
      subtitle={`Every call ${org} received or placed through Teams Phone. Classifications, sentiment, and language come from the transcript, usually 15 to 30 minutes after a call ends.`}
      actions={actions}
      exportConfig={{ name: 'Calls', endpoint: '/export/datasets/Calls', params: { ...callExportParams(filters), title: 'Calls' } }}
      filters={<CallFilterChips filters={filters} categories={categories} agentLabel={agentLabel} queueLabel={queueLabel} onChange={update} />}
    >
      {body}
    </Page>
  );
}
